import {
  companyStatuses,
  companyTypes,
} from "@brainics/shared";
import { z } from "zod";

export const companyTypeSchema = z.enum(companyTypes);
export const companyStatusSchema = z.enum(companyStatuses);

export const companySlugSchema = z
  .string()
  .trim()
  .min(2)
  .max(100)
  .transform((value) => value.toLowerCase())
  .pipe(z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/));

export const companyDisplayNameSchema = z.string().trim().min(2).max(200);
export const companyLegalNameSchema = z.string().trim().min(2).max(255);
export const companyDescriptionSchema = z.string().trim().min(1).max(5_000);
export const companyIndustrySchema = z.string().trim().min(1).max(160);
export const companyWebsiteSchema = z.string().trim().url().max(2_048);
export const companyContactEmailSchema = z
  .string()
  .trim()
  .email()
  .max(320)
  .transform((value) => value.toLowerCase());
export const companyContactPhoneSchema = z
  .string()
  .trim()
  .min(7)
  .max(32)
  .regex(/^\+?[0-9 ()-]+$/);
export const companyCountryCodeSchema = z
  .string()
  .trim()
  .length(2)
  .transform((value) => value.toUpperCase())
  .pipe(z.string().regex(/^[A-Z]{2}$/));
export const companyCitySchema = z.string().trim().min(1).max(120);
export const companyAddressSchema = z.string().trim().min(1).max(500);
export const companyTaxIdSchema = z.string().trim().min(2).max(64);
export const companyRegistrationNumberSchema = z.string().trim().min(2).max(64);

export const companyProfileFieldsSchema = z.object({
  displayName: companyDisplayNameSchema,
  legalName: companyLegalNameSchema.nullable(),
  slug: companySlugSchema,
  description: companyDescriptionSchema.nullable(),
  websiteUrl: companyWebsiteSchema.nullable(),
  industry: companyIndustrySchema.nullable(),
  contactEmail: companyContactEmailSchema.nullable(),
  contactPhone: companyContactPhoneSchema.nullable(),
  countryCode: companyCountryCodeSchema.nullable(),
  city: companyCitySchema.nullable(),
  addressLine: companyAddressSchema.nullable(),
  taxId: companyTaxIdSchema.nullable(),
  registrationNumber: companyRegistrationNumberSchema.nullable(),
});

export const companySchema = companyProfileFieldsSchema.extend({
  id: z.string().uuid(),
  type: companyTypeSchema,
  status: companyStatusSchema,
  ownerUserId: z.string().uuid(),
  statusReason: z.string().max(2_000).nullable(),
  statusChangedAt: z.string().datetime(),
  submittedAt: z.string().datetime().nullable(),
  verifiedAt: z.string().datetime().nullable(),
  blockedAt: z.string().datetime().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const optionalCompanyProfileFieldsSchema = companyProfileFieldsSchema
  .omit({ displayName: true, slug: true })
  .partial();

export const companyOnboardingCreateSchema = optionalCompanyProfileFieldsSchema.extend({
  displayName: companyDisplayNameSchema,
  slug: companySlugSchema,
});

export const companyOnboardingUpdateSchema = companyProfileFieldsSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: "At least one company field must be provided",
  });

export const companyOnboardingRequiredFields = [
  "displayName",
  "legalName",
  "slug",
  "description",
  "industry",
  "contactEmail",
  "countryCode",
  "city",
  "addressLine",
  "taxId",
  "registrationNumber",
] as const;

export const companyOnboardingRequiredFieldSchema = z.enum(
  companyOnboardingRequiredFields,
);

export const companyOnboardingCompletionSchema = z.object({
  complete: z.boolean(),
  missingFields: z.array(companyOnboardingRequiredFieldSchema),
});

export const companyOnboardingResponseSchema = z.object({
  company: companySchema,
  completion: companyOnboardingCompletionSchema,
});

export type CompanyType = z.infer<typeof companyTypeSchema>;
export type CompanyStatus = z.infer<typeof companyStatusSchema>;
export type CompanyProfileFields = z.infer<typeof companyProfileFieldsSchema>;
export type Company = z.infer<typeof companySchema>;
export type CompanyOnboardingCreate = z.infer<typeof companyOnboardingCreateSchema>;
export type CompanyOnboardingUpdate = z.infer<typeof companyOnboardingUpdateSchema>;
export type CompanyOnboardingRequiredField =
  (typeof companyOnboardingRequiredFields)[number];
export type CompanyOnboardingCompletion = z.infer<
  typeof companyOnboardingCompletionSchema
>;
export type CompanyOnboardingResponse = z.infer<
  typeof companyOnboardingResponseSchema
>;
